/**
 * evolutionModel — logique partagée de la page Staff evolution et de son
 * impression. Garantit un visuel/chiffres identiques entre écran et PDF.
 *
 *   - formatters suisses
 *   - buildEvolutionSeries(contracts, prevYear, refYear, csRate) → séries 24 mois + axe
 *   - buildEvolutionModel(metrics, prevYear, refYear) → { hero, bridge, tiles }
 */
import { formatNumber } from '../../utils/format';
import { p, tp, useP } from '../../i18n/useP';

export const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const ALL_M = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/* ── Formatters ──────────────────────────────────────────────────────────── */
export const fmtInt = (v) => formatNumber(v, 0);
export const fmtFte = (v, d = 1) => formatNumber(v, d);

export function fmtIntSigned(v) {
  const n = Number(v) || 0;
  if (n > 0) return `+${fmtInt(n)}`;
  if (n < 0) return `−${fmtInt(Math.abs(n))}`;
  return fmtInt(0);
}
export function fmtFteSigned(v, d = 1) {
  const n = Number(v) || 0;
  if (n > 0) return `+${fmtFte(n, d)}`;
  if (n < 0) return `−${fmtFte(Math.abs(n), d)}`;
  return fmtFte(0, d);
}
export function fmtPct(decimal, d = 1) {
  const n = (Number(decimal) || 0) * 100;
  return `${formatNumber(n, d)}%`;
}
export function fmtPctSigned(decimal, d = 1) {
  const n = (Number(decimal) || 0) * 100;
  if (n > 0) return `+${formatNumber(n, d)}%`;
  if (n < 0) return `−${formatNumber(Math.abs(n), d)}%`;
  return `${formatNumber(0, d)}%`;
}
export function fmtPoints(decimal, d = 1) {
  const n = (Number(decimal) || 0) * 100;
  if (n > 0) return `+${formatNumber(n, d)} pts`;
  if (n < 0) return `−${formatNumber(Math.abs(n), d)} pts`;
  return `${formatNumber(0, d)} pts`;
}

/* ── Séries graphique 24 mois ────────────────────────────────────────────── */
function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function contractActiveInMonth(c, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd   = new Date(year, monthIdx + 1, 0);
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}
function fteMonthly(contracts, year) {
  return ALL_M.map(m =>
    contracts.reduce((acc, c) => acc + (contractActiveInMonth(c, year, m) ? Number(c.activity_rate || 0) : 0), 0));
}

// Brut mensuel N-1 / N : fourni par le backend (metrics.monthly_mass_n_1 /
// monthly_mass_n = profil RÉEL de la Synthèse, compléments inclus, 13e/14e
// posés sur leur mois). L'EPT mensuel reste dérivé des contrats côté client.
export function buildEvolutionSeries(contracts, prevYear, refYear, csRate = 0, monthlyN1 = [], monthlyN = []) {
  const fteN1     = fteMonthly(contracts, prevYear);
  const fteN      = fteMonthly(contracts, refYear);
  const series24    = [...(monthlyN1 || []).map(Number), ...(monthlyN || []).map(Number)];
  const series24fte = [...fteN1, ...fteN];
  const series24cost = series24.map(v => v * (1 + csRate));

  const maxRaw = Math.max(...series24, 1);
  const minRaw = Math.min(...series24.filter(v => v > 0), maxRaw);
  const axisMin = Math.max(0, minRaw * 0.8);
  const axisMax = maxRaw * 1.05;
  return { series24, series24cost, series24fte, axisMin, axisMax };
}

/* ── Modèle synthèse (hero + bridge + tuiles KPI) ────────────────────────── */
export function buildEvolutionModel(metrics, prevYear, refYear, unit = 'chf') {
  const m = metrics || {};
  // Unité d'affichage : seuls les MONTANTS ABSOLUS (coûts, effets volume/prix) se
  // divisent en kCHF. Les ratios CHF/FTE restent en CHF (comme un CHF/h).
  const sc  = (v) => (unit === 'kchf' ? v / 1000 : v);
  const lbl = unit === 'kchf' ? 'kCHF' : 'CHF';
  const costN     = Number(m.total_employer_cost_n || 0);
  const costN1    = Number(m.total_employer_cost_n_1 || 0);
  const deltaCost = costN - costN1;
  const deltaCostPct = costN1 ? (deltaCost / costN1) : 0;

  const fteAvgN  = Number(m.fte_avg_n || 0);
  const fteAvgN1 = Number(m.fte_avg_n_1 || 0);
  const deltaFte = fteAvgN - fteAvgN1;
  const deltaFtePct = fteAvgN1 ? (deltaFte / fteAvgN1) : 0;

  const hcN  = Number(m.headcount_end_n || 0);
  const hcN1 = Number(m.headcount_end_n_1 || 0);
  const deltaHc = hcN - hcN1;
  const deltaHcPct = hcN1 ? (deltaHc / hcN1) : 0;

  const cpfN  = Number(m.cost_per_fte_n || 0);
  const cpfN1 = Number(m.cost_per_fte_n_1 || 0);
  const deltaCpf = cpfN - cpfN1;
  const deltaCpfPct = cpfN1 ? (deltaCpf / cpfN1) : 0;

  const volEffect   = Number(m.volume_effect || 0);
  const priceEffect = Number(m.price_effect || 0);
  const totalDelta = volEffect + priceEffect;
  const volPct   = totalDelta ? Math.abs(volEffect) / Math.abs(totalDelta) : 0;
  const pricePct = totalDelta ? Math.abs(priceEffect) / Math.abs(totalDelta) : 0;

  const arrN  = Number(m.arrivals_n || 0);
  const arrN1 = Number(m.arrivals_n_1 || 0);
  const depN  = Number(m.departures_n || 0);
  const depN1 = Number(m.departures_n_1 || 0);
  const turnN  = Number(m.turnover_n || 0);
  const turnN1 = Number(m.turnover_n_1 || 0);
  const deltaTurn = turnN - turnN1;

  const csRateN  = Number(m.social_charges_rate_n || 0);
  const csRateN1 = Number(m.social_charges_rate_n_1 || 0);
  const deltaCsRate = csRateN - csRateN1;
  const csRateStable = Math.abs(deltaCsRate) < 0.001;

  // Convention couleur de la page : une baisse favorable (coût, turnover, taux
  // de charges) ressort en sage ; tout le reste est neutre (navy). Jamais de
  // terracotta — il n'y a pas d'alerte sur cette page. Le pont volume/prix est
  // une décomposition, pas une favorabilité → ses montants restent navy.
  const costDeltaSign = deltaCost < 0 ? 'positive' : 'neutral';
  const turnDeltaSign = deltaTurn < 0 ? 'positive' : 'neutral';
  const volColor   = '#2B203E';
  const priceColor = '#2B203E';

  const hero = {
    year: refYear,
    mainLabel: `${p('Coût total employeur ')}${refYear}`,
    mainValue: `${fmtInt(sc(costN))} ${lbl}`,
    comparisonValue: `${fmtInt(sc(costN1))} ${lbl}`,
    deltaText: `${fmtIntSigned(sc(deltaCost))} ${lbl}`,
    deltaPctText: fmtPctSigned(deltaCostPct),
    deltaSign: costDeltaSign,
    sublineRight: totalDelta !== 0 ? `${p('dont volume ')}${fmtPct(volPct, 0)}${p(' · prix ')}${fmtPct(pricePct, 0)}` : null,
  };

  const bridge = [
    { label: `${p('Ancrage ')}${prevYear}`, value: `${fmtInt(sc(costN1))} ${lbl}`, subline: `${fmtFte(fteAvgN1)} EPT · ${fmtInt(cpfN1)} CHF/EPT` },
    { op: '+', label: p('Effet volume'), value: `${fmtIntSigned(sc(volEffect))} ${lbl}`, valueColor: volColor, subline: `ΔEPT ${fmtFteSigned(deltaFte)} × ${fmtInt(cpfN1)} CHF/EPT` },
    { op: '+', label: p('Effet prix'), value: `${fmtIntSigned(sc(priceEffect))} ${lbl}`, valueColor: priceColor, subline: `ΔCHF/EPT ${fmtIntSigned(deltaCpf)} × ${fmtFte(fteAvgN)} EPT` },
    { op: '=', label: `${p('Ancrage ')}${refYear}`, value: `${fmtInt(sc(costN))} ${lbl}`, subline: `${fmtFte(fteAvgN)} EPT · ${fmtInt(cpfN)} CHF/EPT` },
  ];

  const tiles = [
    { label: p('EPT moyen'), value: fmtFte(fteAvgN), cmp: fmtFte(fteAvgN1), delta: `${fmtFteSigned(deltaFte)} · ${fmtPctSigned(deltaFtePct)}`, sign: 'neutral' },
    { label: p('Headcount fin'), value: `${hcN}`, cmp: `${hcN1}`, delta: `${fmtIntSigned(deltaHc)} · ${fmtPctSigned(deltaHcPct)}`, sign: 'neutral' },
    { label: tp('phrases:p_7771863046'), value: `${arrN}`, cmp: `${arrN1}`, delta: fmtIntSigned(arrN - arrN1), sign: 'neutral' },
    { label: tp('phrases:p_58d0d1e64a'), value: `${depN}`, cmp: `${depN1}`, delta: fmtIntSigned(depN - depN1), sign: 'neutral' },
    { label: p('Rotation (brute)'), value: fmtPct(turnN, 1), cmp: fmtPct(turnN1, 1), delta: fmtPoints(deltaTurn, 1), sign: turnDeltaSign },
    { label: tp('phrases:p_21c92dab87'), value: `${fmtInt(cpfN)} CHF`, cmp: `${fmtInt(cpfN1)} CHF`, delta: `${fmtIntSigned(deltaCpf)} · ${fmtPctSigned(deltaCpfPct)}`, sign: deltaCpf < 0 ? 'positive' : 'neutral' },
    { label: p('Charges sociales'), value: fmtPct(csRateN, 1), cmp: fmtPct(csRateN1, 1), delta: csRateStable ? p('stable') : fmtPoints(deltaCsRate, 2), sign: deltaCsRate < 0 ? 'positive' : 'neutral' },
  ];

  return { hero, bridge, tiles, csRateN };
}
